import RuianAddressSelector from '@/components/RuianAddressSelector'
import { ConfirmModal } from '@/components/ConfirmModal'
import { DashboardHeader } from '@/containers/dashboard/DashboardHeader'
import ICOOptions from '@/containers/forms/person/ICOOptions'
import { PersonID } from '@/containers/forms/person/PersonID'
import PersonType from '@/containers/forms/person/PersonType'
import { useCreateDummyPerson, useDeleteDummyPerson, useGetDummyPersons, useUpdateDummyPerson } from '@/hooks/useDummyPersons'
import { PageWrapper } from '@/layouts/components/PageWrapper'
import {
  Box, Button, Flex, Grid, HStack, Icon, IconButton,
  Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay,
  Stack, Table, Tbody, Td, Text, Th, Thead, Tooltip, Tr, useDisclosure, useToast,
} from '@chakra-ui/react'
import { faBuilding, faBriefcase, faCopy, faPlus, faUser } from '@fortawesome/pro-light-svg-icons'
import { faTrash } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { CheckboxFormControl, EmptyTablePlaceholder, InputFormControl, TableSkeleton } from 'BrokerTsLib/src/components'
import type { PersonAdditionalData } from 'BrokerTsLib/src/dtos'
import { Selection } from 'BrokerTsLib/src/enums/Selection'
import { useForm } from 'BrokerTsLib/src/rhf'
import { useEffect, useState } from 'react'
import { FormProvider } from 'react-hook-form'
import { DummyPersonModal } from './DummyPersonModal'
import { useAuth } from '@/context/AuthContext'
import CustomTooltip from 'BrokerTsLib/src/components/CustomTooltip'

const personTypeIcon = (type: Selection) => {
  if (type === Selection.Company) return faBuilding
  if (type === Selection.SelfEmployed) return faBriefcase
  return faUser
}

const personTypeLabel = (type: Selection) => {
  if (type === Selection.Company) return 'Právnická osoba'
  if (type === Selection.SelfEmployed) return 'OSVČ'
  return 'Fyzická osoba'
}

const getDisplayName = (p: PersonAdditionalData): string => {
  if (p.type === Selection.Company) return p.companyName ?? '–'
  return [p.titleBefore, p.name, p.surname, p.titleAfter].filter(Boolean).join(' ') || '–'
}

const getDisplayIdentifier = (p: PersonAdditionalData): string => {
  if (p.type === Selection.Company) return p.idendificationNumber ?? '–'
  if (p.type === Selection.SelfEmployed) return p.birthNumber + ' / ' + p.idendificationNumber
  return p.birthNumber ?? '–'
}

const getDisplayAddress = (p: PersonAdditionalData): string => {
  const a = p.address
  if (!a) return '–'
  const street = [a.street, a.streetNumber].filter(Boolean).join(' ')
  const parts = [street || null, a.postCode, a.city].filter(Boolean)
  return parts.join(', ') || '–'
}

export const DummyPersonsPage = () => {
  const { data: persons, isLoading, refetch } = useGetDummyPersons()
  const { mutateAsync: createPerson, isPending: isCreating } = useCreateDummyPerson()
  const { mutateAsync: updatePerson, isPending: isUpdating } = useUpdateDummyPerson()
  const { mutateAsync: deletePerson } = useDeleteDummyPerson()
  const toast = useToast()

  const formDisclosure = useDisclosure()
  const confirmDisclosure = useDisclosure()
  const [selectedPerson, setSelectedPerson] = useState<PersonAdditionalData | null>(null)
  const [deleteId, setDeleteId] = useState<number | null>(null)

  const handleCreate = () => {
    setSelectedPerson(null)
    formDisclosure.onOpen()
  }

  const handleEdit = (person: PersonAdditionalData) => {
    setSelectedPerson(person)
    formDisclosure.onOpen()
  }

  const handleDeleteClick = (id: number) => {
    setDeleteId(id)
    confirmDisclosure.onOpen()
  }

  const handleDeleteConfirm = async (confirmed: boolean) => {
    confirmDisclosure.onClose()
    if (!confirmed || deleteId === null) return
    try {
      await deletePerson(deleteId)
      await refetch()
      toast({ title: 'Klient byl smazán', status: 'success', duration: 3000 })
    } catch {
      toast({ title: 'Nepodařilo se smazat klienta', status: 'error', duration: 4000 })
    }
  }

  const handleSave = async (data: PersonAdditionalData) => {
    try {
      if (selectedPerson?.id) {
        await updatePerson(data)
        toast({ title: 'Klient byl upraven', status: 'success', duration: 3000 })
      } else {
        await createPerson(data)
        toast({ title: 'Klient byl přidán', status: 'success', duration: 3000 })
      }
      formDisclosure.onClose()
      await refetch()
    } catch {
      toast({ title: 'Nepodařilo se uložit klienta', status: 'error', duration: 4000 })
    }
  }

  return (
    <PageWrapper
      header={<DashboardHeader title="Správa dummy klientů" />}
      mainProps={{ sx: { pt: { base: 0, md: 8 }, gap: 6, display: 'grid' } }}
    >
      <Box bg="white" p={6} borderRadius="lg" shadow="sm">
        <Flex justify="flex-end" mb={4}>
          <Button
            colorScheme="primary"
            leftIcon={<Icon as={FontAwesomeIcon} icon={faPlus} />}
            onClick={handleCreate}
          >
            Přidat klienta
          </Button>
        </Flex>

        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>Id</Th>
              <Th w="48px">Typ</Th>
              <Th>Jméno / Název firmy</Th>
              <Th>RČ / IČO</Th>
              <Th>Adresa</Th>
              <Th>Kontakt</Th>
              <Th w="80px" />
            </Tr>
          </Thead>
          <Tbody>
            {isLoading ? (
              <TableSkeleton colSpan={7} rowsCount={5} />
            ) : !persons?.length ? (
              <Tr>
                <Td colSpan={7}><EmptyTablePlaceholder /></Td>
              </Tr>
            ) : persons.map(person => (
              <Tr
                key={person.id}
                cursor="pointer"
                _hover={{ bg: 'gray.100' }}
                onClick={() => handleEdit(person)}
              >
                <Td onClick={e => e.stopPropagation()}>
                  <HStack spacing={1}>
                    <CustomTooltip label="Kopírovat ID" placement="top">
                      <Flex
                        gap={1}
                        onClick={() => {
                          navigator.clipboard.writeText(String(person.id))
                          toast({
                            title: `Id klienta ${person.id} zkopírováno`,
                            status: 'success',
                            duration: 2000,
                          })
                        }}
                      >
                        <IconButton
                          aria-label="Kopírovat ID"
                          icon={<Icon as={FontAwesomeIcon} icon={faCopy} />}
                          size="xs"
                          variant="ghost"
                          colorScheme="gray"
                        />
                        <Text fontWeight="bold">{person.id}</Text>
                      </Flex>
                    </CustomTooltip>
                  </HStack>
                </Td>
                <Td>
                  <Tooltip label={personTypeLabel(person.type)} placement="right">
                    <Icon
                      as={FontAwesomeIcon}
                      icon={personTypeIcon(person.type)}
                      color="primary.500"
                      fontSize="lg"
                    />
                  </Tooltip>
                </Td>
                <Td>
                  <Text fontWeight="medium">{getDisplayName(person)}</Text>
                </Td>
                <Td >
                  <Text fontSize="xs" >{getDisplayIdentifier(person)}</Text>
                </Td>
                <Td fontSize="sm">{getDisplayAddress(person)}</Td>
                <Td fontSize="sm">
                  <Stack spacing={0}>
                    {person.phone && <Text>{person.phone}</Text>}
                    {person.email && <Text>{person.email}</Text>}
                    {!person.phone && !person.email && '–'}
                  </Stack>
                </Td>
                <Td onClick={e => e.stopPropagation()}>
                  <HStack justify="flex-end" spacing={1}>
                    <IconButton
                      aria-label="Smazat"
                      p={2}
                      icon={<Icon as={FontAwesomeIcon} icon={faTrash} />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => handleDeleteClick(person.id!)}
                    />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      <DummyPersonModal
        isOpen={formDisclosure.isOpen}
        onClose={formDisclosure.onClose}
        person={selectedPerson}
        onSave={handleSave}
        isSaving={isCreating || isUpdating}
      />

      <ConfirmModal
        isOpen={confirmDisclosure.isOpen}
        onClose={handleDeleteConfirm}
        title="Smazat dummy klienta"
        yesButtonContent="Smazat"
        noButtonContent="Zrušit"
      >
        Opravdu chcete smazat tohoto dummy klienta? Tato akce je nevratná.
      </ConfirmModal>
    </PageWrapper>
  )
}

